import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import { isUiDebugLoggingEnabled } from '@/services/logger';
import { networkMonitor } from '@/services/networkMonitor';
import {
  archiveMaybeItemAndFetch,
  createMaybeItemAndFetch,
  getMaybeItems,
  markMaybeItemScheduledAndFetch,
  subscribeToMaybeItems,
  type MaybeItem,
  type MaybeItemTag,
  type MaybeItemSource,
} from '@/services/maybeItems';

const OPTIMISTIC_PREFIX = '__optimistic_';

const matchesMaybeItem = (optimistic: MaybeItem, serverItem: MaybeItem): boolean => {
  if (!optimistic || !serverItem) return false;
  if (optimistic.id === serverItem.id) return true;
  const optTitle = optimistic.title?.toLowerCase?.() ?? '';
  const serverTitle = serverItem.title?.toLowerCase?.() ?? '';
  const createdDelta = Math.abs((optimistic.createdAt?.getTime?.() ?? 0) - (serverItem.createdAt?.getTime?.() ?? 0));
  return optTitle === serverTitle && optimistic.createdBy === serverItem.createdBy && createdDelta < 60000;
};

type MaybeItemInput = {
  title: string;
  description?: string;
  source: MaybeItemSource;
  sourceId?: string;
  tags: MaybeItemTag[];
  createdBy: string;
};

type MaybePendingState = {
  creatingIds: Set<string>;
  schedulingIds: Set<string>;
  archivingIds: Set<string>;
};

type UseMaybeItemsParams = {
  tripId: string;
  userId: string | null;
  status?: 'pending' | 'all';
  refreshToken?: number;
};

type CreateResult = {
  id: string;
  items: MaybeItem[];
};

const buildOptimisticItem = (
  tripId: string,
  input: MaybeItemInput,
  optimisticId: string,
): MaybeItem => ({
  id: optimisticId,
  tripId,
  title: input.title,
  description: input.description,
  source: input.source,
  sourceId: input.sourceId,
  tags: input.tags,
  createdBy: input.createdBy,
  createdAt: new Date(),
  decidedAt: undefined,
  status: 'pending',
});

export function useMaybeItems({ tripId, userId, status = 'pending', refreshToken }: UseMaybeItemsParams) {
  const [serverItems, setServerItems] = useState<MaybeItem[]>([]);
  const [optimisticItems, setOptimisticItems] = useState<MaybeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<MaybePendingState>({
    creatingIds: new Set<string>(),
    schedulingIds: new Set<string>(),
    archivingIds: new Set<string>(),
  });

  const pendingCreateRef = useRef<Map<string, Promise<CreateResult>>>(new Map());

  const refresh = useCallback(async () => {
    if (!tripId) return;
    try {
      const items = await getMaybeItems(tripId, status);
      setServerItems(items);
    } finally {
      setLoading(false);
    }
  }, [tripId, status]);

  useEffect(() => {
    if (!tripId) return;
    setLoading(true);
    void refresh();
    const unsubscribe = subscribeToMaybeItems(
      tripId,
      (items) => {
        setServerItems(items);
        setLoading(false);
      },
      { status },
    );
    return () => unsubscribe?.();
  }, [tripId, refresh, status]);

  useEffect(() => {
    if (typeof refreshToken !== 'number') return;
    void refresh();
  }, [refreshToken, refresh]);

  useEffect(() => {
    if (!tripId) return;

    const handleAppState = (state: AppStateStatus) => {
      if (state === 'active') {
        void refresh();
      }
    };

    const subscription = AppState.addEventListener('change', handleAppState);
    const unsubscribeNetwork = networkMonitor.addListener((status) => {
      if (status.isConnected) {
        void refresh();
      }
    });

    return () => {
      subscription.remove();
      unsubscribeNetwork();
    };
  }, [refresh, tripId]);

  const mergedItems = useMemo(() => {
    if (optimisticItems.length === 0) return serverItems;
    const filteredServer = serverItems.filter(
      (serverItem) => !optimisticItems.some((opt) => matchesMaybeItem(opt, serverItem)),
    );
    return [...optimisticItems, ...filteredServer];
  }, [optimisticItems, serverItems]);

  // Clean up optimistic items once they appear in serverItems. Only call setOptimisticItems when
  // the filtered list actually shrinks to avoid an infinite loop (setState with new array ref every time).
  useEffect(() => {
    if (optimisticItems.length === 0 || serverItems.length === 0) return;
    const filtered = optimisticItems.filter((opt) => !serverItems.some((serverItem) => matchesMaybeItem(opt, serverItem)));
    if (filtered.length !== optimisticItems.length) {
      setOptimisticItems(filtered);
    }
  }, [optimisticItems, serverItems]);

  const addMaybeItem = useCallback(
    async (input: MaybeItemInput): Promise<string | null> => {
      if (!tripId || !userId) return null;
      const title = input.title.trim();
      if (!title) return null;

      const optimisticId = `${OPTIMISTIC_PREFIX}${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const optimistic = buildOptimisticItem(tripId, { ...input, title }, optimisticId);

      setOptimisticItems((prev) => [optimistic, ...prev]);
      setPending((prev) => {
        const next = new Set(prev.creatingIds);
        next.add(optimisticId);
        return { ...prev, creatingIds: next };
      });

      if (isUiDebugLoggingEnabled()) {
        console.log('[MaybeHook] Creating maybe item', { tripId, optimisticId, title });
      }

      const createPromise = createMaybeItemAndFetch(tripId, {
        title,
        description: input.description,
        source: input.source,
        sourceId: input.sourceId,
        tags: input.tags,
        createdBy: input.createdBy,
      });

      pendingCreateRef.current.set(optimisticId, createPromise);

      let keepOptimistic = false;
      try {
        const result = await createPromise;
        const hasMatch = result.items.some(
          (serverItem) => serverItem.id === result.id || matchesMaybeItem(optimistic, serverItem),
        );
        if (result.items.length > 0) {
          setServerItems(result.items);
        }
        if (!hasMatch) {
          keepOptimistic = true;
          if (isUiDebugLoggingEnabled()) {
            console.log('[MaybeHook] Created item not yet in list; keeping optimistic until refresh', {
              tripId,
              optimisticId,
            });
          }
          setTimeout(() => void refresh(), 750);
        }
        return result.id;
      } finally {
        pendingCreateRef.current.delete(optimisticId);
        if (!keepOptimistic) {
          setOptimisticItems((prev) => prev.filter((it) => it.id !== optimisticId));
        }
        setPending((prev) => {
          const next = new Set(prev.creatingIds);
          next.delete(optimisticId);
          return { ...prev, creatingIds: next };
        });
      }
    },
    [refresh, tripId, userId],
  );

  const resolveOptimisticItem = useCallback(
    async (item: MaybeItem): Promise<MaybeItem | null> => {
      if (!item.id.startsWith(OPTIMISTIC_PREFIX)) return item;
      const pendingPromise = pendingCreateRef.current.get(item.id);
      if (pendingPromise) {
        try {
          const result = await pendingPromise;
          const created =
            result.items.find((i) => i.id === result.id) ||
            result.items.find(
              (i) =>
                i.title.toLowerCase() === item.title.toLowerCase() &&
                i.createdBy === item.createdBy,
            );
          return created ?? null;
        } catch {
          return null;
        }
      }
      await refresh();
      const match =
        serverItems.find((i) => i.id === item.id) ||
        serverItems.find(
          (i) => i.title.toLowerCase() === item.title.toLowerCase() && i.createdBy === item.createdBy,
        );
      return match ?? null;
    },
    [refresh, serverItems],
  );

  const scheduleItem = useCallback(
    async (item: MaybeItem, onScheduleItem?: (item: MaybeItem) => Promise<boolean> | boolean) => {
      if (!tripId) return false;
      const pendingId = item.id;
      setPending((prev) => {
        const next = new Set(prev.schedulingIds);
        next.add(pendingId);
        return { ...prev, schedulingIds: next };
      });

      if (isUiDebugLoggingEnabled()) {
        console.log('[MaybeHook] Schedule requested', { tripId, itemId: item.id, optimistic: item.id.startsWith(OPTIMISTIC_PREFIX) });
      }

      try {
        const didSchedule = await (onScheduleItem?.(item) ?? false);
        if (didSchedule === false) return false;
        const resolved = item.id.startsWith(OPTIMISTIC_PREFIX) ? await resolveOptimisticItem(item) : item;
        if (!resolved) {
          await refresh();
          return false;
        }
        const items = await markMaybeItemScheduledAndFetch(tripId, resolved.id);
        setServerItems(items);
        return true;
      } finally {
        setPending((prev) => {
          const next = new Set(prev.schedulingIds);
          next.delete(pendingId);
          return { ...prev, schedulingIds: next };
        });
      }
    },
    [refresh, resolveOptimisticItem, tripId],
  );

  const archiveItem = useCallback(
    async (item: MaybeItem) => {
      if (!tripId) return false;
      const pendingId = item.id;
      setPending((prev) => {
        const next = new Set(prev.archivingIds);
        next.add(pendingId);
        return { ...prev, archivingIds: next };
      });
      if (isUiDebugLoggingEnabled()) {
        console.log('[MaybeHook] Archive requested', { tripId, itemId: item.id, optimistic: item.id.startsWith(OPTIMISTIC_PREFIX) });
      }
      try {
        const resolved = item.id.startsWith(OPTIMISTIC_PREFIX) ? await resolveOptimisticItem(item) : item;
        if (!resolved) {
          await refresh();
          return false;
        }
        const items = await archiveMaybeItemAndFetch(tripId, resolved.id);
        setServerItems(items);
        return true;
      } finally {
        setPending((prev) => {
          const next = new Set(prev.archivingIds);
          next.delete(pendingId);
          return { ...prev, archivingIds: next };
        });
      }
    },
    [refresh, resolveOptimisticItem, tripId],
  );

  const isItemCreating = useCallback(
    (id: string) => pending.creatingIds.has(id) || id.startsWith(OPTIMISTIC_PREFIX),
    [pending.creatingIds],
  );
  const isItemScheduling = useCallback(
    (id: string) => pending.schedulingIds.has(id),
    [pending.schedulingIds],
  );
  const isItemArchiving = useCallback(
    (id: string) => pending.archivingIds.has(id),
    [pending.archivingIds],
  );

  return {
    items: mergedItems,
    loading,
    pending,
    isItemCreating,
    isItemScheduling,
    isItemArchiving,
    addMaybeItem,
    scheduleItem,
    archiveItem,
    refresh,
    isOnline: networkMonitor.isOnline(),
  };
}
